// Admin · Stats tab — comprehensive KPI grid fed by GET /api/admin/stats.
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { API } from "../../../lib/api";

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="font-mono text-[10px] uppercase tracking-[2px] text-white/55">{title}</h3>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{children}</div>
  </section>
);

const Card = ({ label, value, color = "#fff", testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border p-3"
    style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(249,115,22,0.18)" }}
  >
    <p className="font-mono text-[9px] uppercase tracking-[2px] text-white/45">{label}</p>
    <p className="mt-1 font-mono text-xl font-bold" style={{ color }}>{value ?? "—"}</p>
  </div>
);

const eur = (n) => n != null ? `€${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—";

export default function StatsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setLoading(true);
    fetch(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.clone().json())
      .then((d) => { if (alive) { setData(d); setErr(null); } })
      .catch((e) => { if (alive) setErr(String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 font-mono text-[11px] text-white/55">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando estadísticas…
      </div>
    );
  }
  if (err) return <p className="font-mono text-[11px] text-[#ff3b5c]">Error: {err}</p>;
  if (!data) return null;

  const u = data.usuarios || {};
  const i = data.ingresos || {};
  const act = data.actividad || {};
  const lm = data.liquidity_map || {};
  const sh = data.shadow || {};
  const aca = data.academia || {};
  const rt = data.r_token || {};

  return (
    <div data-testid="admin-tab-stats" className="space-y-6">
      <Section title="Usuarios">
        <Card label="Totales" value={u.total} color="#fff" />
        <Card label="PRO" value={u.pro} color="#00d084" />
        <Card label="Gratuitos" value={u.gratuitos} color="#aaa" />
        <Card label="Conversión %" value={u.tasa_conversion_pro_pct != null ? `${u.tasa_conversion_pro_pct}%` : "—"} color="#f97316" />
        <Card label="Nuevos 24h" value={u.nuevos_hoy} color="#00d084" />
        <Card label="Nuevos 7d" value={u.nuevos_7d} />
        <Card label="Nuevos 30d" value={u.nuevos_30d} />
        <Card label="Bloqueados" value={u.bloqueados} color="#ff3b5c" />
      </Section>

      <Section title="Ingresos">
        <Card label="Total estimado" value={eur(i.total_estimado_eur)} color="#f0c800" />
        <Card label="Stripe pagos" value={i.pro_via_stripe} />
        <Card label="R₿L interno" value={i.pro_via_rbl_interno} />
        <Card label="R₿L on-chain" value={i.pro_via_rbl_onchain} />
        <Card label="Lifetime PRO" value={i.lifetime_pro} color="#00d084" />
        <Card label="Renovaciones 30d" value={i.renovaciones_30d} />
      </Section>

      <Section title="Actividad">
        <Card label="Logins 24h" value={act.logins_24h} />
        <Card label="Logins 7d" value={act.logins_7d} />
        <Card label="Login fallidos 1h" value={act.logins_fallidos_1h} color="#ff8c42" />
        <Card label="API calls 24h" value={act.api_calls_24h} />
      </Section>

      <Section title="Liquidity Map">
        <Card label="Snapshots 24h" value={lm.snapshots_24h} />
        <Card label="Coins activas" value={lm.coins_activas} color="#f97316" />
        <Card label="Predicciones 7d" value={lm.predicciones_7d} />
        <Card label="Tasa acierto 7d" value={lm.tasa_acierto_pct != null ? `${lm.tasa_acierto_pct}%` : "—"} color="#00d084" />
      </Section>

      <Section title="Shadow Network">
        <Card label="Posts totales" value={sh.posts_totales} />
        <Card label="Posts 7d" value={sh.posts_7d} />
        <Card label="Comentarios 7d" value={sh.comentarios_7d} />
        <Card label="Tokens listados" value={sh.tokens_listados} color="#f97316" />
      </Section>

      <Section title="Academia">
        <Card label="Lecciones completadas" value={aca.lecciones_completadas} color="#00d084" />
        <Card label="Diplomas emitidos" value={aca.diplomas_emitidos} />
        <Card label="Usuarios activos 7d" value={aca.usuarios_activos_7d} />
      </Section>

      <Section title="$RE₿EL Token">
        <Card label="Holders" value={rt.holders} color="#f97316" />
        <Card label="Transferencias 7d" value={rt.transferencias_7d} />
        <Card label="Volumen 7d" value={rt.volumen_7d} />
      </Section>
    </div>
  );
}
